import React, { useState } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification, deleteUser } from 'firebase/auth';
import { auth } from '../firebase/config';

const EmailTest = () => {
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const addResult = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setResults(prev => [...prev, { message, type, timestamp }]);
  };

  const testEmailVerification = async () => {
    if (!testEmail) {
      addResult('Please enter a test email', 'error');
      return;
    }

    setLoading(true);
    setResults([]);
    
    try {
      addResult('🧪 Starting direct Firebase email verification test...', 'info');
      addResult(`📧 Test email: ${testEmail}`, 'info');
      
      // Create a temporary user for testing
      const tempPassword = 'TempTest123!';
      addResult('1️⃣ Creating temporary user with Firebase...', 'info');
      
      const userCredential = await createUserWithEmailAndPassword(auth, testEmail, tempPassword);
      const user = userCredential.user;
      addResult(`✅ User created successfully: ${user.uid}`, 'success');
      
      // Test email verification
      addResult('2️⃣ Sending email verification...', 'info');
      await sendEmailVerification(user);
      addResult('✅ sendEmailVerification completed without errors', 'success');
      
      // Check user properties
      addResult('3️⃣ User properties:', 'info');
      addResult(`   - UID: ${user.uid}`, 'info');
      addResult(`   - Email: ${user.email}`, 'info');
      addResult(`   - Email verified: ${user.emailVerified}`, 'info');
      
      // Test with action code settings
      addResult('4️⃣ Trying with action code settings...', 'info');
      const actionCodeSettings = {
        url: window.location.origin + '/email-verification',
        handleCodeInApp: false
      };
      
      await sendEmailVerification(user, actionCodeSettings);
      addResult('✅ sendEmailVerification with actionCodeSettings completed', 'success');
      
      // Clean up - delete the test user
      addResult('5️⃣ Cleaning up test user...', 'info');
      await deleteUser(user);
      addResult('✅ Test user deleted', 'success');
      
      addResult('🎉 Test completed! Check your email inbox and spam folder.', 'success');
      addResult('📋 If no email received, the issue is with Firebase project configuration.', 'warning');
      
    } catch (error) {
      addResult(`❌ Test failed: ${error.message}`, 'error');
      addResult(`Error code: ${error.code}`, 'error');
      console.error('Full error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-4">Email Verification Test</h2>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Email Address:
        </label>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="test@example.com"
        />
      </div>
      
      <button
        onClick={testEmailVerification}
        disabled={loading}
        className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Testing...' : 'Test Email Verification'}
      </button>
      
      {results.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">Test Results:</h3>
          <div className="bg-gray-100 p-4 rounded-md max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div
                key={index}
                className={`mb-2 text-sm ${
                  result.type === 'error' ? 'text-red-600' :
                  result.type === 'success' ? 'text-green-600' :
                  result.type === 'warning' ? 'text-yellow-600' :
                  'text-gray-700'
                }`}
              >
                <span className="text-gray-500 text-xs">[{result.timestamp}]</span> {result.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTest;
